import {Phone, Calendar, Shield, Award, Check, MapPin, Clock, DollarSign} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'
import BeforeAfterSlider from '@/components/BeforeAfterSlider'
import CanonicalTag from '@/components/CanonicalTag'

const PaintProtectionFilmSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    // Add FAQ Schema
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does paint protection film cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PPF pricing in San Diego starts at $499 for front bumper coverage, $1,299 for partial front, $1,899 for full front, $4,499 for full vehicle gloss coverage, $5,499 for matte/stealth finishes, and $5,999 for colored PPF. These are starting prices — exact pricing depends on vehicle size, body complexity, film selection, coverage area, and installation complexity. Large SUVs, trucks, and specialty vehicles may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF last in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium PPF lasts 10+ years in San Diego climate with proper care. The self-healing topcoat maintains clarity and gloss, while UV inhibitors prevent yellowing."
          }
        },
        {
          "@type": "Question",
          "name": "Is PPF worth it in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. San Diego's combination of coastal salt air, intense UV exposure, and busy freeways makes PPF essential for preserving paint and resale value."
          }
        },
        {
          "@type": "Question",
          "name": "Will PPF damage my car's paint?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Premium PPF is designed specifically for automotive paint and removes cleanly without damage. It protects your factory paint underneath."
          }
        },
        {
          "@type": "Question",
          "name": "Can PPF be applied to any vehicle?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. We install PPF on all makes and models including Tesla, BMW, Mercedes, Porsche, Audi, Range Rover, Toyota, Honda, and exotic vehicles."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front: 1 day. Full front: 2 days. Full vehicle: 3-5 days. Installation time varies by vehicle size and coverage."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Set meta tags
    document.title = "Paint Protection Film San Diego | Professional PPF Installation & Pricing"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert paint protection film installation in San Diego. Self-healing PPF, manufacturer-backed film warranties, professional installers. Protect Tesla, BMW, Mercedes, Porsche. Serving Vista, Carlsbad, Oceanside, Encinitas.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/paint-protection-film-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film San Diego - Professional PPF Installation</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your vehicle investment with professional paint protection film installation in San Diego. Our PPF specialists deliver careful coverage using premium self-healing film—preventing rock chips, scratches, and UV damage while maintaining your vehicle's factory finish and resale value.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving San Diego County including Vista, Carlsbad, Oceanside, Encinitas, and Rancho Santa Fe. We specialize in PPF installation for luxury vehicles, sports cars, daily drivers, and commercial fleets using bulk film or precision-cut patterns—selected by vehicle and panel—with wrapped edges where appropriate.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose PPF in San Diego */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Paint Protection Film is Essential in San Diego</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              San Diego's unique climate presents specific challenges for automotive paint. Coastal salt air accelerates oxidation and corrosion, intense year-round UV radiation causes fading and clear coat damage, and busy freeways like I-5, I-15, and Highway 78 expose vehicles to constant rock chips and road debris. Paint protection film provides the ultimate defense against these environmental threats.
            </p>
            <p>
              Our <a href="/paint-protection-film" className="text-cyan-400 hover:underline">professional PPF installation services</a> use premium self-healing film with advanced technology that repairs minor scratches and swirl marks when exposed to heat. This means your vehicle maintains a flawless, showroom-fresh appearance for years—even with daily driving through San Diego County's challenging conditions.
            </p>
            <p>
              Whether you drive a <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla</a>, <a href="/bmw-ppf-san-diego" className="text-cyan-400 hover:underline">BMW</a>, Mercedes, Porsche, or want <a href="/corvette-ppf-san-diego" className="text-cyan-400 hover:underline">Corvette PPF San Diego</a> coverage, PPF installation dramatically increases resale value by preserving factory paint condition. Many luxury and exotic vehicle owners in <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a>, <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, and surrounding communities protect their investments with PPF immediately after purchase.
            </p>
            <p>
              Our Vista-based facility serves all of San Diego County with convenient scheduling, transparent pricing, and professional installation expertise. We combine PPF with complementary services including <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a>, <a href="/window-tinting" className="text-cyan-400 hover:underline">ceramic window tinting</a>, and <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">vinyl wraps</a> for comprehensive vehicle protection and customization.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Installation Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Premium film repairs minor scratches and swirl marks with heat exposure, maintaining a flawless finish indefinitely.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Professional Installers</h3>
              <p className="text-gray-300">Experienced installation team focused on careful technique, clean edge work, and proper manufacturer warranty documentation on every project.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Clock className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Manufacturer-Backed Warranty</h3>
              <p className="text-gray-300">Premium PPF films typically include a manufacturer warranty against yellowing, cracking, peeling, and delamination. Coverage terms depend on the specific film selected.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <DollarSign className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Resale Value</h3>
              <p className="text-gray-300">Preserved factory paint dramatically increases resale value. PPF-protected vehicles command premium prices on the used market.</p>
              </div>
            </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6 text-white text-center">How Much Does PPF Cost in San Diego?</h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
            Paint protection film pricing in San Diego starts at $499 and depends on your vehicle's size, body complexity, the film you choose, your coverage area, paint prep requirements, and installation complexity. Large SUVs, trucks, specialty vehicles, and specialty films may cost more than the starting prices below. Request a free consultation for your exact quote.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-2 text-white">Front Bumper PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $499</div>
              <p className="text-gray-400 text-sm mb-6">Entry-level protection</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Most affordable rock chip protection</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-2 text-white">Partial Front PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Varies by vehicle size</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Side mirrors and headlights</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-xl font-bold mb-2 text-white">Full Front PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Varies by vehicle size</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood and fenders</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Fog lights + manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-2 text-white">Full Vehicle Gloss PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Varies by vehicle size</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete vehicle coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All painted surfaces protected</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Maximum protection & resale value</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-2 text-white">Matte / Stealth PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $5,499</div>
              <p className="text-gray-400 text-sm mb-6">Full vehicle, satin/matte finish</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full vehicle matte or stealth finish</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Same protection as gloss PPF</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Aggressive, modern appearance</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-2 text-white">Colored PPF</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $5,999</div>
              <p className="text-gray-400 text-sm mb-6">Full color change + protection</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete color change</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Self-healing paint protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>See our <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored PPF page</a> for details</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 max-w-3xl mx-auto">
            All prices above are starting prices, not fixed quotes. Add <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a> or <a href="/window-tinting" className="text-cyan-400 hover:underline">window tint</a> for package discounts. Exact pricing depends on your vehicle's size, body complexity, film selection, coverage area, paint prep needs, and installation complexity — large SUVs, trucks, and specialty vehicles may cost more than the starting prices shown. Exact pricing is determined after a free consultation and vehicle inspection.
          </p>
          <div className="text-center mt-10">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Exact Pricing for Your Vehicle
            </button>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Vehicle Inspection</h3>
                <p className="text-gray-300">We begin with a thorough inspection of your vehicle's paint condition, discussing your protection goals, daily driving habits, and budget. We'll explain coverage options, film technologies, and provide a transparent quote with no hidden fees. This consultation typically takes 15-20 minutes and helps us design the perfect PPF solution for your specific vehicle and needs.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Correction</h3>
                <p className="text-gray-300">Professional paint preparation is critical for long-lasting PPF adhesion. We thoroughly wash and decontaminate your vehicle using pH-balanced automotive shampoo, clay bar treatment, and isopropyl alcohol wipe-down. For vehicles with existing imperfections, we perform multi-stage paint correction using compound and polish to remove swirl marks, water spots, and light scratches—ensuring a flawless surface for film application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation, Selected by Vehicle and Panel</h3>
                <p className="text-gray-300">We select bulk film or precision-cut patterns based on the vehicle and panel, then finish with professional wet installation and wrapped edges where appropriate. For complex areas, we properly disassemble trim, badges, and components to achieve complete edge-to-edge coverage with no visible lines or gaps. Installation is completed in a controlled indoor bay to support clean adhesion.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Check, Curing & Delivery</h3>
                <p className="text-gray-300">After installation, your vehicle undergoes a meticulous quality inspection checking for bubbles, proper edge sealing, and complete coverage. We allow proper curing time (24-48 hours) before delivery, ensuring optimal adhesion and durability. You'll receive comprehensive care instructions, warranty documentation, and ongoing support for the lifetime of your PPF. We schedule a complimentary follow-up inspection 30 days after installation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PPF Technology */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Advanced PPF Technology Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Self-Healing Topcoat</h3>
              <p className="text-gray-300 mb-4">Revolutionary self-healing elastomeric polymer topcoat repairs minor scratches, swirl marks, and scuffs when exposed to heat—from the sun or warm water. Light scratches literally disappear, maintaining a perpetually flawless finish.</p>
              <p className="text-gray-400 text-sm">Works on damage up to 10 mils deep</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">UV Protection & Clarity</h3>
              <p className="text-gray-300 mb-4">Advanced UV inhibitors prevent yellowing and degradation in San Diego's intense sunlight. Crystal-clear optical properties maintain your vehicle's factory color and gloss—indistinguishable from unprotected paint when properly installed.</p>
              <p className="text-gray-400 text-sm">99% optical clarity, no orange peel</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Stain Resistance</h3>
              <p className="text-gray-300 mb-4">Hydrophobic topcoat repels water, dirt, and contaminants. Bug splatter, bird droppings, and tree sap wash off easily without staining—critical for San Diego's coastal environment and eucalyptus-lined roads.</p>
              <p className="text-gray-400 text-sm">Easier washing and maintenance</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Rock Chip & Impact Protection</h3>
              <p className="text-gray-300 mb-4">8-mil thick urethane film absorbs impact energy from rock chips, road debris, and parking lot dings that would otherwise damage your paint. The film sacrifices itself to protect your factory finish underneath.</p>
              <p className="text-gray-400 text-sm">Tested to withstand highway speeds</p>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection Film FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does paint protection film cost in San Diego?</h3>
              <p className="text-gray-300">Paint protection film cost San Diego pricing starts at $499 for front bumper coverage, $1,299 for partial front, $1,899 for full front, $4,499 for full vehicle gloss coverage, $5,499 for matte/stealth finishes, and $5,999 for colored PPF. These are starting prices — exact pricing depends on vehicle size, body complexity, film selection, coverage area, prep requirements, and installation complexity. Large SUVs, trucks, and specialty vehicles may cost more. We provide transparent quotes with exact pricing before any work begins.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF last in San Diego?</h3>
              <p className="text-gray-300">Premium PPF lasts 10+ years in San Diego climate with proper care. The self-healing topcoat maintains clarity and gloss, while UV inhibitors prevent yellowing even under intense Southern California sun. Premium films typically include a manufacturer warranty against defects, yellowing, cracking, and peeling — terms depend on the specific film selected.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it in San Diego?</h3>
              <p className="text-gray-300">Absolutely. San Diego's combination of coastal salt air, intense UV exposure, and busy freeways (I-5, I-15, Highway 78) makes PPF essential for preserving paint and resale value. PPF-protected vehicles maintain showroom condition and command significantly higher resale prices. The investment pays for itself when you sell or trade your vehicle.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF damage my car's paint?</h3>
              <p className="text-gray-300">No. Premium PPF is designed specifically for automotive paint and removes cleanly without damage when professionally installed and removed. In fact, it protects your factory paint underneath from UV damage, oxidation, and environmental contaminants. Your paint will be in better condition under the film than exposed paint.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to any vehicle?</h3>
              <p className="text-gray-300">Yes. We install PPF on all makes and models including <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla</a>, BMW, Mercedes, Porsche, Audi, Range Rover, Lexus, Toyota, Honda, and <a href="/corvette-ppf-san-diego" className="text-cyan-400 hover:underline">Corvette Paint Protection Film</a> for C8, Z06, E-Ray, and C7 models. Installation methods are selected for each vehicle and panel so coverage stays clean and complete.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF installation take?</h3>
              <p className="text-gray-300">Partial front: 1 day. Full front: 2 days. Full vehicle: 3-5 days. Installation time varies by vehicle size, coverage selection, and paint condition. We never rush quality work—proper preparation, installation, and curing time support long-term film performance under the applicable manufacturer warranty.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What's included in the PPF warranty?</h3>
              <p className="text-gray-300">Premium PPF typically includes a manufacturer warranty against yellowing, cracking, peeling, delamination, and hazing. Coverage length and terms depend on the film selected. Ask us which warranty applies to your install so expectations stay clear.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I wash my car normally after PPF installation?</h3>
              <p className="text-gray-300">Yes, after the initial 7-day curing period. PPF is highly durable and withstands normal car washes, automatic touchless washes, and hand washing. We recommend pH-neutral soap and microfiber towels. The hydrophobic topcoat makes washing easier—dirt and grime slide off effortlessly.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you offer PPF for commercial fleets?</h3>
              <p className="text-gray-300">Yes. We provide <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">commercial fleet PPF services</a> for business vehicles, delivery vans, and corporate fleets throughout San Diego. Volume discounts available for multiple vehicles. Fleet installations protect your brand image and reduce long-term paint repair costs.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Where is your PPF installation shop located?</h3>
              <p className="text-gray-300">We're located at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084. Conveniently accessible from all of San Diego County including <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a>, <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a>, <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a>, and <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">downtown San Diego</a>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Real Results — Before &amp; After</h2>
            <p className="text-xl text-gray-400">Drag the slider to see the transformation. Real customer projects from our Vista shop.</p>
          </div>

          {/* Primary slider — highest-converting: Range Rover PPF protection story */}
          <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-6 mb-8">
            <div className="mb-4">
              <span className="inline-block bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1 rounded-full mb-3">PROTECTION SUCCESS STORY</span>
              <h3 className="text-2xl font-bold text-white">Range Rover — Satin PPF Caught Real Road Damage</h3>
              <p className="text-gray-400 mt-2 text-sm">This Range Rover's satin PPF absorbed an impact that would have destroyed the factory paint. The film was cleaned and repaired — the paint underneath was completely untouched. This is exactly what PPF is designed to do.</p>
            </div>
            <BeforeAfterSlider
              beforeImage="placeholder:range-rover-satin-ppf-damage"
              afterImage="https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp"
              beforeLabel="Damage Caught by PPF"
              afterLabel="PPF Protected — Paint Preserved"
              beforeAlt="Range Rover PPF absorbing road damage — paint protected underneath"
              afterAlt="Range Rover after satin PPF installation by Mr. Wrap Vista"
              projectTitle="Range Rover — Full Body Satin Paint Protection Film · Vista, CA"
              serviceBadge="Full Body Satin PPF"
              showCta
              className="max-w-3xl mx-auto"
            />
          </div>

          {/* Secondary sliders row — Tesla + Porsche installs */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-5">
              <h3 className="text-lg font-bold text-white mb-1">Tesla Model 3 — Full Front PPF</h3>
              <p className="text-gray-400 text-sm mb-4">Before installation vs. showroom-fresh result after full front coverage.</p>
              <BeforeAfterSlider
                beforeImage="placeholder:tesla-model3-ppf-before"
                afterImage="https://static.lumi.new/c5/c569c3f9888a7a4fed4a4801f7c73daa.webp"
                beforeLabel="Before PPF"
                afterLabel="After PPF"
                beforeAlt="Tesla Model 3 before PPF installation"
                afterAlt="Tesla Model 3 after full front PPF installation"
                projectTitle="Tesla Model 3 — Full Front PPF"
                serviceBadge="Full Front PPF"
              />
            </div>
            <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-5">
              <h3 className="text-lg font-bold text-white mb-1">Porsche Macan — Full Vehicle PPF</h3>
              <p className="text-gray-400 text-sm mb-4">In-progress installation vs. finished wrap — invisible protection.</p>
              <BeforeAfterSlider
                beforeImage="placeholder:porsche-macan-ppf-inprogress"
                afterImage="https://static.lumi.new/52/5234166494ce9fbf1dd6bad26161ad9e.webp"
                beforeLabel="During Install"
                afterLabel="Finished"
                beforeAlt="Porsche Macan during PPF installation process"
                afterAlt="Porsche Macan after white PPF installation"
                projectTitle="Porsche Macan — Full Vehicle PPF"
                serviceBadge="Full Body PPF"
              />
            </div>
          </div>

          <p className="text-center text-gray-500 text-xs mt-6">
            Real customer photos uploading soon. Send your before &amp; after content to <a href="tel:858-282-8119" className="text-cyan-400 hover:underline">858-282-8119</a> to feature your project.
          </p>
        </div>
      </section>

      {/* Embedded Quote Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmbeddedQuoteForm
            defaultService="ppf"
            heading="Get a Free PPF Quote"
            subheading="Tell us about your vehicle and coverage goals. Our Vista team will provide a transparent, no-pressure estimate tailored to your needs."
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Combine PPF with these popular services for complete vehicle protection:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">San Diego PPF Services</h3>
              <p className="text-gray-400 text-sm">Local paint protection film installation throughout San Diego County and North County areas</p>
            </a>
            <a href="/colored-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Color change paint protection film in gloss, satin, matte, and stealth finishes</p>
            </a>
            <a href="/ppf-vista" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Vista PPF Installation</h3>
              <p className="text-gray-400 text-sm">Our Vista facility offers convenient PPF services for North County San Diego residents</p>
            </a>
            <a href="/ppf-vs-ceramic-coating-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">PPF vs Ceramic Coating San Diego</h3>
              <p className="text-gray-400 text-sm">Should you get PPF or ceramic coating? Compare protection, cost, and longevity</p>
            </a>
            <a href="/bmw-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">BMW PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Paint protection film for BMW sedans, SUVs, and M cars, with tint available in the same shop</p>
            </a>
            <a href="/corvette-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Corvette PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Paint protection film for C8 Stingray, Z06, E-Ray, and C7 Corvettes</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving All of San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Based in <a href="/ppf-vista" className="text-cyan-400 hover:underline font-semibold">Vista</a>, we serve the entire San Diego region with professional paint protection film installation.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop)</li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Poway</li>
                <li>• Ramona</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Central & South County</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown</li>
                <li>• Chula Vista</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Protect Your Vehicle with Professional PPF?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free paint protection film consultation today. Get a custom quote based on your vehicle and protection needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default PaintProtectionFilmSanDiegoPage
